import { RefreshCw, ShieldAlert } from 'lucide-react';
import PageHeader from '../../shell/PageHeader';
import TrendChart from '../../ui/TrendChart';
import { Alert, Button, Card } from '../../ui';
import { formatUtc } from '../../../utils/datetime';
import useDashboard from './dashboard/useDashboard';
import { PeriodCaption, PeriodControls } from './dashboard/PeriodControls';
import SummaryGrid from './dashboard/SummaryGrid';
import IssuingAcquiringPanel from './dashboard/IssuingAcquiringPanel';
import TodayPanel from './dashboard/TodayPanel';
import OperationsPanel from './dashboard/OperationsPanel';
import TopIssuedUsers from './dashboard/TopIssuedUsers';
import RecentTransactions from './dashboard/RecentTransactions';

const muted = 'text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor';

const breadcrumbs = [{ label: 'Home', to: '/layout/dashboard' }, { label: 'Dashboard' }];

const Dashboard = () => {
  const { params, updateParams, refetch, data, loading, error, paramError, coverageNote, credential, metricOptions } =
    useDashboard();

  const period = data?.period;

  const controls = <PeriodControls params={params} onChange={updateParams} />;

  const metricSelect = (
    <select
      className="form-select form-select-sm w-auto"
      aria-label="Chart metric"
      value={params.metric}
      onChange={(event) => updateParams({ metric: event.target.value })}
    >
      {metricOptions.map((option) => (
        <option key={option.key} value={option.key}>
          {option.label}
        </option>
      ))}
    </select>
  );

  return (
    <>
      <PageHeader title="Dashboard" breadcrumbs={breadcrumbs} actions={controls} />

      {paramError && (
        <Alert variant="warning" className="mb-6">
          {paramError}
        </Alert>
      )}

      {credential?.needsAttention && (
        <Alert variant={credential.expired ? 'danger' : 'warning'} className="mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <ShieldAlert className="w-4 h-4 shrink-0" aria-hidden="true" />
            <strong>API credentials: {credential.label}</strong>
            <span>
              ({formatUtc(credential.expiresAt)}). Server-to-server requests{' '}
              {credential.expired ? 'are being rejected' : 'will start failing'} until the credentials are rotated in
              Settings.
            </span>
          </div>
        </Alert>
      )}

      {error ? (
        <Alert variant="danger">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{error}</span>
            <Button variant="outline-danger" size="sm" onClick={refetch}>
              <RefreshCw className="w-4 h-4" aria-hidden="true" />
              Retry
            </Button>
          </div>
        </Alert>
      ) : (
        <>
          <PeriodCaption period={period} />

          {coverageNote &&
            (coverageNote.stale ? (
              <Alert variant="warning" className="mb-4">
                {coverageNote.message}
              </Alert>
            ) : (
              <p className={`mb-4 text-sm ${muted}`}>{coverageNote.message}</p>
            ))}

          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12">
              <SummaryGrid metrics={data?.summary} loading={loading} />
            </div>

            <div className="col-span-12">
              <Card title={data?.series?.label ?? 'Trend'} className="mb-0" actions={metricSelect}>
                <TrendChart series={data?.series} loading={loading} />
              </Card>
            </div>

            <div className="col-span-12">
              <IssuingAcquiringPanel issuing={data?.issuing} acquiring={data?.acquiring} loading={loading} />
            </div>

            <div className="col-span-12 xl:col-span-9">
              <OperationsPanel operations={data?.operations} credential={credential} loading={loading} />
            </div>

            <div className="col-span-12 xl:col-span-3">
              <TodayPanel today={data?.today} loading={loading} />
            </div>

            <div className="col-span-12">
              <TopIssuedUsers
                rows={data?.top_issued_users}
                loading={loading}
                top={params.top}
                onTopChange={(top) => updateParams({ top })}
              />
            </div>

            <div className="col-span-12">
              <RecentTransactions rows={data?.recent_transactions} loading={loading} />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Dashboard;
